import { isTerminalExecutionState } from "../execution-state";
import {
  classifyAwaitingUserPhase,
  isParkedExecution,
} from "../tracking/park-policy";
import type { ExecutionFeedOperation } from "../tracking/feed-operation";
import type {
  ExecutionDisplayContext,
  ExecutionHandle,
  ExecutionSummary,
} from "../types";

/**
 * Product-voice copy for the center execution toast, ported from v1's tense
 * engine. Resolution priority per field: submit-time displayContext → live
 * feed refs (display sub-objects the tRPC schema strips) → generic.
 */

export type ExecutionToastAction = NonNullable<
  ExecutionDisplayContext["action"]
>;

export type ExecutionToastStatus =
  | "loading"
  | "action_required"
  | "parked"
  | "success"
  | "error"
  | "cancelled";

export const PRESENT_CONTINUOUS: Record<ExecutionToastAction, string> = {
  buy: "Buying",
  sell: "Selling",
  deposit: "Depositing",
  withdraw: "Withdrawing",
  bridge: "Bridging",
  swap: "Swapping",
  rebalance: "Rebalancing",
  transfer: "Transferring",
};

export const PAST_TENSE: Record<ExecutionToastAction, string> = {
  buy: "Bought",
  sell: "Sold",
  deposit: "Deposited",
  withdraw: "Withdrew",
  bridge: "Bridged",
  swap: "Swapped",
  rebalance: "Rebalanced",
  transfer: "Transferred",
};

export const NOUN_FORM: Record<ExecutionToastAction, string> = {
  buy: "Buy",
  sell: "Sell",
  deposit: "Deposit",
  withdraw: "Withdrawal",
  bridge: "Bridge",
  swap: "Swap",
  rebalance: "Rebalance",
  transfer: "Transfer",
};

const SUCCESS_LABEL: Record<ExecutionToastAction, string> = {
  buy: "Buy complete",
  sell: "Sell complete",
  deposit: "Deposit complete",
  withdraw: "Withdrawal complete",
  bridge: "Bridge complete",
  swap: "Swap complete",
  rebalance: "Rebalance complete",
  transfer: "Transfer complete",
};

export interface ExecutionToastCopySource {
  displayContext: ExecutionDisplayContext | null;
  handle: ExecutionHandle;
  liveOperation: ExecutionFeedOperation | null;
  summary: ExecutionSummary | null;
}

function readRecord(
  value: unknown,
  key: string,
): Record<string, unknown> | null {
  if (!value || typeof value !== "object") {
    return null;
  }

  const child = (value as Record<string, unknown>)[key];
  return child && typeof child === "object" && !Array.isArray(child)
    ? (child as Record<string, unknown>)
    : null;
}

function readString(
  value: Record<string, unknown> | null,
  key: string,
): string | null {
  const raw = value?.[key];
  return typeof raw === "string" && raw.trim().length > 0 ? raw : null;
}

function getPhase(source: ExecutionToastCopySource): string | null {
  return (
    source.summary?.phase ??
    source.handle.progress?.phase ??
    source.liveOperation?.currentPhase ??
    null
  );
}

export function deriveExecutionToastStatus(
  source: ExecutionToastCopySource,
): ExecutionToastStatus {
  const state = source.handle.state;

  if (state === "completed") {
    return "success";
  }
  if (state === "failed") {
    return "error";
  }
  if (state === "cancelled") {
    return "cancelled";
  }
  if (isParkedExecution(state, getPhase(source))) {
    return "parked";
  }
  if (state === "awaiting_user") {
    return classifyAwaitingUserPhase(getPhase(source)) === "action_required"
      ? "action_required"
      : "parked";
  }

  return "loading";
}

/**
 * The action verb family for this operation. displayContext wins (the flow
 * knows exactly what the user did); market orders fall back to
 * refs.marketOrder.intentSide; everything else maps from the operation kind.
 */
export function resolveExecutionToastAction(
  source: ExecutionToastCopySource,
): ExecutionToastAction {
  if (source.displayContext?.action) {
    return source.displayContext.action;
  }

  const marketOrder =
    readRecord(source.liveOperation?.refs, "marketOrder") ??
    readRecord(source.handle.refs, "marketOrder");
  const intentSide = readString(marketOrder, "intentSide");
  if (intentSide === "buy" || intentSide === "sell") {
    return intentSide;
  }

  switch (source.handle.kind) {
    case "deposit":
      return "deposit";
    case "withdraw":
      return "withdraw";
    case "transfer":
      return "transfer";
    case "bridge":
      return "bridge";
    case "rebalance":
      return "rebalance";
    case "swap":
    case "execution":
      return "swap";
  }
}

export interface ExecutionToastDisplay {
  amountDisplay: string | null;
  assetId: string | null;
  chainId: number | null;
  symbol: string | null;
  tokenIconUrl: string | null;
  tokenIcons: Array<{ url: string; symbol: string }> | null;
}

/**
 * Display facts (symbol, human amount, icon hints) for the pill and modal.
 * Market orders and deposits carry no display refs server-side today, so for
 * them everything beyond intentSide comes from displayContext (or stays
 * generic on recovered/cross-tab operations).
 */
export function resolveExecutionToastDisplay(
  source: ExecutionToastCopySource,
): ExecutionToastDisplay {
  const context = source.displayContext;
  // The user feed carries full refs; the tRPC handle carries the display-safe
  // projection (post S2). Prefer the feed frame, fall back to the handle so
  // engine-op recoveries get product voice too.
  const liveRefs = source.liveOperation?.refs ?? null;
  const handleRefs = source.handle.refs;
  const withdrawRefs =
    readRecord(liveRefs, "withdraw") ?? readRecord(handleRefs, "withdraw");
  const transferRefs =
    readRecord(liveRefs, "transfer") ?? readRecord(handleRefs, "transfer");
  const bridgeRefs =
    readRecord(liveRefs, "bridge") ?? readRecord(handleRefs, "bridge");
  const marketOrderRefs =
    readRecord(liveRefs, "marketOrder") ??
    readRecord(handleRefs, "marketOrder");
  const displayRefs = withdrawRefs ?? transferRefs ?? bridgeRefs;

  const intentSide = readString(marketOrderRefs, "intentSide");
  const marketOrderAssetId =
    intentSide === "sell"
      ? readString(marketOrderRefs, "sellAssetId")
      : readString(marketOrderRefs, "buyAssetId");

  const assetId =
    context?.assetId ??
    readString(withdrawRefs, "assetId") ??
    readString(transferRefs, "assetId") ??
    readString(bridgeRefs, "assetId") ??
    marketOrderAssetId;

  const chainIdRaw = marketOrderRefs?.chainId ?? withdrawRefs?.chainId;

  return {
    amountDisplay:
      context?.amountDisplay ?? readString(displayRefs, "displayAmount"),
    assetId,
    chainId:
      context?.chainId ??
      (typeof chainIdRaw === "number" && Number.isFinite(chainIdRaw)
        ? chainIdRaw
        : null),
    symbol: context?.symbol ?? readString(displayRefs, "assetSymbol"),
    tokenIconUrl: context?.tokenIconUrl ?? null,
    tokenIcons:
      context?.tokenIcons && context.tokenIcons.length > 0
        ? context.tokenIcons
        : null,
  };
}

export function formatToastAmount(amount: string | number): string {
  const num = typeof amount === "string" ? parseFloat(amount) : amount;

  if (Number.isNaN(num)) {
    return amount.toString();
  }
  if (num === 0) {
    return "0";
  }
  if (num < 0.0001 && num > 0) {
    return "<0.0001";
  }
  if (num >= 1_000_000_000) {
    return `${(num / 1_000_000_000).toFixed(2)}B`;
  }
  if (num >= 1_000_000) {
    return `${(num / 1_000_000).toFixed(2)}M`;
  }
  if (num >= 1_000) {
    return `${(num / 1_000).toFixed(2)}K`;
  }
  if (num >= 1) {
    return num.toFixed(2);
  }

  return num.toFixed(4).replace(/\.?0+$/u, "");
}

/** Amounts that are already human-formatted (e.g. "$500", "2.5 ETH", "3 assets")
 * pass through untouched; bare numerics get v1's compact formatting. */
function formatDisplayAmount(amountDisplay: string): string {
  return /^[\d.,]+$/u.test(amountDisplay.trim())
    ? formatToastAmount(amountDisplay.replace(/,/gu, ""))
    : amountDisplay.trim();
}

/**
 * The pill headline. v1 wording, extended with the park/action-required
 * states v1 rendered as an eternal spinner.
 */
export function buildExecutionToastLabel(
  status: ExecutionToastStatus,
  action: ExecutionToastAction,
  display: ExecutionToastDisplay,
): string {
  const symbolSuffix = display.symbol ? ` ${display.symbol}` : "";

  switch (status) {
    case "loading":
      return `${PRESENT_CONTINUOUS[action]}${symbolSuffix}...`;
    case "action_required":
      return "Waiting for your transfer";
    case "parked":
      return `${NOUN_FORM[action]} still processing`;
    case "success": {
      if (display.amountDisplay) {
        const formatted = formatDisplayAmount(display.amountDisplay);
        if (!formatted.startsWith("<")) {
          // Currency amounts read as "Bought $500 of AAPL"; unit amounts as
          // "Bought 0.5 ETH".
          const joiner =
            formatted.startsWith("$") && display.symbol ? " of" : "";
          return `${PAST_TENSE[action]} ${formatted}${joiner}${symbolSuffix}`;
        }
      }
      return SUCCESS_LABEL[action];
    }
    case "error":
      return `${NOUN_FORM[action]} failed`;
    case "cancelled":
      return `${NOUN_FORM[action]} cancelled`;
  }
}

const ERROR_PATTERNS: Array<[RegExp, string]> = [
  [/insufficient (funds|balance)/iu, "Insufficient balance"],
  [/user (rejected|denied|cancelled|canceled)/iu, "Request was cancelled"],
  [/slippage/iu, "Price moved too much — try again"],
  [/(timeout|timed out)/iu, "The request timed out — try again"],
  [/network|connection/iu, "Network issue — check your connection"],
];

const MAX_ERROR_LENGTH = 140;

/** Friendly error text: known patterns get product copy, everything else is
 * passed through capped (server terminal failures are already sanitized). */
export function parseExecutionErrorMessage(
  raw: string | null | undefined,
): string | null {
  if (!raw || raw.trim().length === 0) {
    return null;
  }

  for (const [pattern, friendly] of ERROR_PATTERNS) {
    if (pattern.test(raw)) {
      return friendly;
    }
  }

  const trimmed = raw.trim();
  return trimmed.length > MAX_ERROR_LENGTH
    ? `${trimmed.slice(0, MAX_ERROR_LENGTH - 3).trimEnd()}...`
    : trimmed;
}

/**
 * Secondary line for the expanded modal (never shown on the pill). Parked and
 * action-required states get fixed product copy; errors get the classified
 * message; otherwise nothing (the timeline speaks for itself).
 */
export function buildExecutionToastDetail(
  status: ExecutionToastStatus,
  source: ExecutionToastCopySource,
): string | null {
  if (status === "parked") {
    return "Safe to close — we'll keep watching and let you know.";
  }
  if (status === "action_required") {
    return "Complete the transfer in your wallet to continue.";
  }
  if (status === "error") {
    return parseExecutionErrorMessage(
      source.summary?.terminalReason ?? source.handle.error,
    );
  }

  return null;
}

export function isTerminalToastStatus(status: ExecutionToastStatus): boolean {
  return status === "success" || status === "error" || status === "cancelled";
}

export { isTerminalExecutionState };
